import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  formatPrice as formatProductPrice,
  siteConfig,
} from "../Datas/productData";
import "../css/cartpage.scss";

const toNumericPrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;
  const parsedPrice = Number(String(price || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsedPrice) ? parsedPrice : 0;
};

const formatCurrency = (value) => {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  return formatProductPrice(amount);
};

const findProduct = (productId) =>
  productData.find((product) => String(product.id) === String(productId));

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart, removeFromCart, updateQuantity } =
    useContext(CartContext);

  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = findProduct(item.id);
          if (!product) return null;

          const quantity = Math.max(1, Math.floor(Number(item.quantity) || 1));
          const unitPrice = toNumericPrice(product.price);
          return {
            ...item,
            product,
            quantity,
            unitPrice,
            lineTotal: unitPrice * quantity,
          };
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = cartLines.reduce(
    (runningTotal, item) => runningTotal + item.lineTotal,
    0
  );
  const itemCount = cartLines.reduce(
    (runningTotal, item) => runningTotal + item.quantity,
    0
  );
  const freeShippingThreshold = Math.max(
    0,
    Number(siteConfig.freeShippingThreshold) || 0
  );
  const flatShippingRate = Math.max(
    0,
    Number(siteConfig.shippingFlatRate) || 0
  );
  const qualifiesForFreeShipping =
    freeShippingThreshold === 0 || subtotal >= freeShippingThreshold;
  const shipping = qualifiesForFreeShipping ? 0 : flatShippingRate;
  const total = subtotal + shipping;
  const amountUntilFreeShipping = Math.max(
    0,
    freeShippingThreshold - subtotal
  );
  const shippingProgress = freeShippingThreshold
    ? Math.min(100, (subtotal / freeShippingThreshold) * 100)
    : 100;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCheckout = () => {
    navigate("/checkout");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (cartLines.length === 0) {
    return (
      <main className="cart-page cart-page--empty" id="main-content" tabIndex="-1">
        <section className="cart-empty" aria-labelledby="empty-cart-title">
          <p className="cart-empty__eyebrow">Your bag</p>
          <h1 id="empty-cart-title">Your cart is empty</h1>
          <p>
            Discover elevated essentials designed to bridge the gap between
            streetwear and luxury.
          </p>
          <Link to="/shop" onClick={scrollToTop} className="cart-primary-link">
            Discover the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content" tabIndex="-1">
      <header className="cart-page__heading">
        <div>
          <p className="cart-page__eyebrow">{siteConfig.brandName}</p>
          <h1>Shopping bag</h1>
        </div>
        <p className="cart-page__count">
          {itemCount} {itemCount === 1 ? "item" : "items"}
        </p>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__products" aria-label="Products in bag">
          <div className="cart-page__products-toolbar">
            <span>Your selection</span>
            <button type="button" onClick={clearCart}>
              Clear bag
            </button>
          </div>

          <div className="cart-items">
            {cartLines.map((item) => {
              const productImage =
                item.product.images?.[0] || item.product.image || "";
              const variantKey = `${item.id}-${item.size || "default"}-${
                item.color || "default"
              }`;
              const stockLimit = Number(item.product.stock);
              const atStockLimit =
                Number.isFinite(stockLimit) && item.quantity >= stockLimit;

              return (
                <article className="cart-item" key={variantKey}>
                  <Link
                    className="cart-item__image-link"
                    to={`/shopDetails/${item.product.id}`}
                    onClick={scrollToTop}
                    aria-label={`View ${item.product.name}`}
                  >
                    <img
                      className="cart-item__image"
                      src={productImage}
                      alt={item.product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>

                  <div className="cart-item__content">
                    <div className="cart-item__topline">
                      <div>
                        <p className="cart-item__category">
                          {item.product.category}
                        </p>
                        <Link
                          className="cart-item__name"
                          to={`/shopDetails/${item.product.id}`}
                          onClick={scrollToTop}
                        >
                          {item.product.name}
                        </Link>
                      </div>
                      <p className="cart-item__total">
                        {formatCurrency(item.lineTotal)}
                      </p>
                    </div>

                    <dl className="cart-item__variants">
                      {item.color && (
                        <div>
                          <dt>Colour</dt>
                          <dd>{item.color}</dd>
                        </div>
                      )}
                      {item.size && (
                        <div>
                          <dt>Size</dt>
                          <dd>{item.size}</dd>
                        </div>
                      )}
                    </dl>

                    <div className="cart-item__bottomline">
                      <div className="cart-item__quantity-wrap">
                        <div className="cart-quantity" aria-label="Quantity">
                          <button
                            type="button"
                            aria-label={`Decrease quantity of ${item.product.name}`}
                            disabled={item.quantity <= 1}
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                item.size,
                                item.color,
                                item.quantity - 1
                              )
                            }
                          >
                            −
                          </button>
                          <span aria-live="polite">{item.quantity}</span>
                          <button
                            type="button"
                            aria-label={`Increase quantity of ${item.product.name}`}
                            disabled={atStockLimit}
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                item.size,
                                item.color,
                                item.quantity + 1
                              )
                            }
                          >
                            +
                          </button>
                        </div>
                        {atStockLimit && (
                          <span className="cart-item__stock" role="status">
                            Maximum available
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="cart-item__remove"
                        onClick={() =>
                          removeFromCart(item.id, item.size, item.color)
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <Link to="/shop" onClick={scrollToTop} className="cart-back-link">
            ← Continue shopping
          </Link>
        </section>

        <aside className="cart-summary" aria-labelledby="order-summary-title">
          <h2 id="order-summary-title">Order summary</h2>

          <div className="cart-shipping-progress">
            <p>
              {qualifiesForFreeShipping
                ? "You've unlocked free shipping."
                : `Spend ${formatCurrency(
                    amountUntilFreeShipping
                  )} more to unlock free shipping.`}
            </p>
            <div
              className="cart-shipping-progress__track"
              role="progressbar"
              aria-label="Free shipping progress"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(shippingProgress)}
            >
              <span style={{ width: `${shippingProgress}%` }} />
            </div>
          </div>

          <div className="cart-summary__rows">
            <div>
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div>
              <span>Shipping</span>
              <span>{shipping === 0 ? "Free" : formatCurrency(shipping)}</span>
            </div>
          </div>

          <div className="cart-summary__total">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
          <p className="cart-summary__notice">
            Taxes and duties are calculated at checkout where applicable.
          </p>

          <button
            type="button"
            className="cart-checkout-button"
            onClick={handleCheckout}
          >
            Checkout
          </button>
          <p className="cart-summary__secure">100% secure checkout</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
